/**
 * 已解析 URL 的媒体灯箱。舞台、玻璃工具条、旋转和缩略图对齐 IM 查看器。
 * 缩放、翻页和点按关闭由 yet-another-react-lightbox 承担。
 * 消息、权限和 URL 解析留在调用方。
 */
import { MinusIcon, PlusIcon, XIcon } from 'lucide-react';
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import Lightbox, {
  type GenericSlide,
  type Slide,
  type SlideImage,
  type ZoomRef,
} from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

import { type PortalContainer, usePortalContainer } from '../../config-context';
import { cn } from '../../lib/utils';
import { Icon } from './icon';
import {
  createDismissPlugin,
  createImagePointerGuardPlugin,
  TAP_DOUBLE_CLICK_MS,
} from './media-lightbox-dismiss';

export interface MediaLightboxItem {
  id: string;
  kind: 'image' | 'video';
  /** 缺失时灯箱显示不可用，不替调用方补地址。 */
  url?: string;
  name?: string;
  mime?: string;
  thumbnail?: string;
  /**
   * 视频正中的清晰占位。有二级缩略图时用它，不用消息里那张模糊的一级图。
   */
  poster?: string;
  /** 地址还在解析时为 true，灯箱显示加载而不是不可用。 */
  pending?: boolean;
}

export interface MediaLightboxLabels {
  close?: string;
  previous?: string;
  next?: string;
  zoomIn?: string;
  zoomOut?: string;
  zoom?: string;
  rotate?: string;
  reset?: string;
  copy?: string;
  unavailable?: string;
  play?: string;
  pause?: string;
  progress?: string;
  mute?: string;
  unmute?: string;
  expand?: string;
  restore?: string;
  loading?: string;
  retry?: string;
}

export interface MediaLightboxProps {
  items: readonly MediaLightboxItem[];
  activeId: string;
  onClose: () => void;
  onActiveIdChange?: (id: string) => void;
  onCopy?: (item: MediaLightboxItem) => void;
  /** 调用方自己的动作，画在右上角关闭钮左边。 */
  toolbar?: ReactNode;
  /** 左上角内容，例如发送者。 */
  header?: ReactNode;
  /** 底部说明。 */
  caption?: ReactNode;
  /** 画在父级里面，不另开一层遮罩。右键菜单需要灯箱留在触发器内。 */
  contained?: boolean;
  /** 为 true 时收起工具条。 */
  chromeHidden?: boolean;
  canPrevious?: boolean;
  canNext?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  /** 当前项没有地址时的重试。 */
  onRetry?: (item: MediaLightboxItem) => void;
  labels?: MediaLightboxLabels;
}

const DEFAULT_LABELS: Required<MediaLightboxLabels> = {
  close: 'Close',
  previous: 'Previous',
  next: 'Next',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  zoom: 'Zoom',
  rotate: 'Rotate',
  reset: 'Reset',
  copy: 'Copy',
  unavailable: 'This media is unavailable',
  play: 'Play',
  pause: 'Pause',
  progress: 'Playback progress',
  mute: 'Mute',
  unmute: 'Unmute',
  expand: 'Expand',
  restore: 'Restore',
  loading: 'Loading',
  retry: 'Retry',
};

/** 点这些后代不关预览。图片本身要点一下就关。 */
const KEEP =
  'video, button, input, [data-media-lightbox-toolbar], [role="slider"]';

const PLUGINS = [
  createImagePointerGuardPlugin(),
  Zoom,
  createDismissPlugin(KEEP),
];

const GLASS_CLASS =
  'rounded-full border border-white/10 bg-[linear-gradient(rgba(57,57,57,0.86),rgba(57,57,57,0.86)),linear-gradient(rgba(18,18,18,0.96),rgba(18,18,18,0.96))] shadow-[0_2px_6px_rgba(0,0,0,0.04),inset_0_40px_30px_-40px_rgba(230,230,230,0.7)]';

/** 顶栏、说明、缩放条一起淡入淡出。只动透明度，不给颜色加过渡。 */
const CHROME_FADE =
  'transition-opacity duration-150 ease-out motion-reduce:transition-none';

function chromeClass(hidden: boolean): string {
  return cn(CHROME_FADE, hidden && 'opacity-0 [&_*]:pointer-events-none');
}

const RANGE_CLASS =
  'm-0 h-[33px] w-[101px] cursor-pointer appearance-none bg-transparent px-2 disabled:cursor-default disabled:opacity-35 [&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-[3px] [&::-webkit-slider-runnable-track]:bg-[linear-gradient(to_right,#fff_0_var(--media-range,0%),rgba(120,120,120,0.2)_var(--media-range,0%)_100%)] [&::-webkit-slider-thumb]:mt-[-5px] [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[0.5px] [&::-webkit-slider-thumb]:border-[#ccc] [&::-webkit-slider-thumb]:bg-white/92 [&::-webkit-slider-thumb]:shadow-[0_1px_4px_rgba(0,0,0,0.4),inset_0_3px_1px_-2px_#fff]';

interface VideoSlide extends GenericSlide {
  type: 'video';
  sources: { src: string; type?: string }[];
  poster?: string;
}

interface FailedSlide extends SlideImage {
  failed?: boolean;
  pending?: boolean;
  itemId?: string;
  poster?: string;
}

declare module 'yet-another-react-lightbox' {
  interface SlideTypes {
    video: VideoSlide;
  }
}

function portalElement(
  container: PortalContainer | undefined,
): HTMLElement | undefined {
  if (!container || typeof document === 'undefined') {
    return undefined;
  }
  if (container instanceof HTMLElement) {
    return container;
  }
  if ('current' in container && container.current instanceof HTMLElement) {
    return container.current;
  }
  return undefined;
}

export function MediaLightbox({
  items,
  activeId,
  onClose,
  onActiveIdChange,
  onCopy,
  toolbar,
  header,
  caption,
  contained = false,
  chromeHidden = false,
  canPrevious,
  canNext,
  onPrevious,
  onNext,
  onRetry,
  labels,
}: MediaLightboxProps) {
  const copy = { ...DEFAULT_LABELS, ...labels };
  const portal = portalElement(usePortalContainer());
  const zoomRef = useRef<ZoomRef>(null);
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoNode, setVideoNode] = useState<HTMLVideoElement | null>(null);
  /**
   * 只绑当前这一页。回调身份保持稳定，避免每次渲染先被 React 传 null 把节点丢掉。
   * 邻项预载时 `loadedmetadata` 已经发生，切过来不会再来一次，所以绑上后立刻读时长。
   */
  const bindVideoRef = useRef((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    setVideoNode((current) => (current === node ? current : node));
  });
  /** 这次切到视频后还没真正开始播。用户按暂停后清掉，避免元数据事件把片子又拉起来。 */
  const wantPlayRef = useRef(true);
  const [videoPaused, setVideoPaused] = useState(true);
  const [videoMuted, setVideoMuted] = useState(false);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoExpanded, setVideoExpanded] = useState(false);
  const [thumbRow, setThumbRow] = useState<HTMLDivElement | null>(null);
  const [captionNode, setCaptionNode] = useState<HTMLDivElement | null>(null);
  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.id === activeId),
  );
  const [index, setIndex] = useState(activeIndex);
  const slides = useMemo(() => items.map((item) => toSlide(item)), [items]);
  const active = items[index];
  const minZoom = zoomRef.current?.minZoom ?? 1;
  const maxZoom = zoomRef.current?.maxZoom ?? 15;
  const zoomable = active?.kind === 'image' && Boolean(active.url);
  const transformed = rotation !== 0 || zoom > minZoom + 0.01;
  const backdrop =
    active?.poster ||
    active?.thumbnail ||
    (active?.kind === 'image' ? active.url : undefined);
  // 当前时间按总时长的字数留宽。00:00 是 5 个字，超过 99 分钟会更长。
  const clockSlot = `${Math.max(5, formatClock(videoDuration).length)}ch`;

  useEffect(() => {
    setIndex(activeIndex);
  }, [activeIndex]);

  // `autoPlay` 只在元素第一次挂上时生效。邻项预载时 offset 不是 0，切过来改属性不会再播。
  // 点缩略图是用户手势，在布局阶段调用 play() 才能带上声音通过自动播放限制。
  useLayoutEffect(() => {
    const node = videoRef.current;
    if (active?.kind !== 'video' || !active.url || !node) {
      return;
    }
    void node.play().catch(() => {
      setVideoPaused(true);
    });
    return () => {
      node.pause();
    };
  }, [index, active?.kind, active?.url]);

  useEffect(() => {
    if (!videoNode) {
      return;
    }
    const sync = () => {
      const duration = videoNode.duration;
      if (Number.isFinite(duration) && duration > 0) {
        setVideoDuration((current) =>
          current === duration ? current : duration,
        );
      }
      const time = videoNode.currentTime;
      if (Number.isFinite(time)) {
        setVideoCurrentTime((current) => (current === time ? current : time));
      }
    };
    sync();
    videoNode.addEventListener('timeupdate', sync);
    videoNode.addEventListener('durationchange', sync);
    videoNode.addEventListener('loadedmetadata', sync);
    videoNode.addEventListener('loadeddata', sync);
    videoNode.addEventListener('seeked', sync);
    return () => {
      videoNode.removeEventListener('timeupdate', sync);
      videoNode.removeEventListener('durationchange', sync);
      videoNode.removeEventListener('loadedmetadata', sync);
      videoNode.removeEventListener('loadeddata', sync);
      videoNode.removeEventListener('seeked', sync);
    };
  }, [videoNode]);

  useEffect(() => {
    if (!thumbRow) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const delta = thumbWheelDelta(event, thumbRow);
      if (delta !== 0) {
        thumbRow.scrollLeft += delta;
      }
    };
    thumbRow.addEventListener('wheel', onWheel, { passive: false });
    return () => thumbRow.removeEventListener('wheel', onWheel);
  }, [thumbRow]);

  useEffect(() => {
    if (!captionNode) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      // 灯箱在容器上 preventDefault 滚轮用来缩放。说明超高时把事件留在文字上，才能滚文字。
      if (captionNode.scrollHeight - captionNode.clientHeight <= 1) {
        return;
      }
      event.stopPropagation();
    };
    captionNode.addEventListener('wheel', onWheel);
    return () => captionNode.removeEventListener('wheel', onWheel);
  }, [captionNode]);

  if (items.length === 0 || (!contained && !portal)) {
    return null;
  }

  const select = (next: number) => {
    const item = items[next];
    if (!item) {
      return;
    }
    if (index !== next) {
      setRotation(0);
      setZoom(1);
      setVideoPaused(true);
      setVideoCurrentTime(0);
      setVideoDuration(0);
      setVideoExpanded(false);
      wantPlayRef.current = item.kind === 'video';
    }
    setIndex(next);
    onActiveIdChange?.(item.id);
  };

  const stage = (
    <div
      ref={setHost}
      className={contained ? 'absolute inset-0' : 'fixed inset-0 z-50'}
      data-slot="media-lightbox"
      style={{ '--media-rotation': `${rotation}deg` } as CSSProperties}
    >
      {host ? (
        <Lightbox
          open
          close={onClose}
          index={index}
          slides={slides}
          plugins={PLUGINS}
          portal={{
            root: host,
            container: {
              className: cn(
                'media-lightbox [--yarl__color_backdrop:#000] [--yarl__color_button:rgba(255,255,255,0.9)] [--yarl__color_button_active:#fff] [--yarl__color_button_disabled:rgba(255,255,255,0.28)] [--yarl__navigation_button_padding:0]',
                videoExpanded && 'media-lightbox-video-expanded',
              ),
              style: {
                '--media-rotation': `${rotation}deg`,
                '--media-backdrop': backdrop ? `url("${backdrop}")` : 'none',
              } as CSSProperties,
            },
          }}
          on={{
            view: ({ index: next }) => select(next),
            zoom: ({ zoom: value }) => setZoom(value),
          }}
          controller={{ closeOnBackdropClick: false }}
          carousel={{
            finite: true,
            preload: 1,
            imageProps: { crossOrigin: 'anonymous' },
          }}
          zoom={{
            ref: zoomRef,
            maxZoomPixelRatio: 15,
            zoomInMultiplier: 1.5,
            doubleClickDelay: TAP_DOUBLE_CLICK_MS,
            scrollToZoom: true,
            pinchZoomV4: true,
          }}
          toolbar={{ buttons: [] }}
          labels={{
            Previous: copy.previous,
            Next: copy.next,
            Close: copy.close,
            'Zoom in': copy.zoomIn,
            'Zoom out': copy.zoomOut,
          }}
          render={{
            buttonPrev: () => (
              <NavButton
                label={copy.previous}
                direction="prev"
                disabled={onPrevious ? canPrevious === false : index <= 0}
                onClick={() => (onPrevious ? onPrevious() : select(index - 1))}
              />
            ),
            buttonNext: () => (
              <NavButton
                label={copy.next}
                direction="next"
                disabled={
                  onNext ? canNext === false : index >= items.length - 1
                }
                onClick={() => (onNext ? onNext() : select(index + 1))}
              />
            ),
            controls: () => (
              <>
                <div
                  data-media-lightbox-toolbar
                  className={cn(
                    'pointer-events-none absolute inset-x-0 top-0 z-1002 flex min-h-19.25 items-center justify-between px-4 py-3',
                    chromeClass(chromeHidden),
                  )}
                >
                  <div className="pointer-events-auto min-w-0">{header}</div>
                  <div className="pointer-events-auto flex items-center gap-2.5">
                    {toolbar}
                    {onCopy && active ? (
                      <GlassButton
                        label={copy.copy}
                        onClick={() => onCopy(active)}
                      >
                        {copy.copy}
                      </GlassButton>
                    ) : null}
                    <div
                      className={cn(
                        GLASS_CLASS,
                        'flex size-10 items-center justify-center',
                      )}
                    >
                      <GlassButton label={copy.close} onClick={onClose}>
                        <XIcon className="size-6" />
                      </GlassButton>
                    </div>
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-1002 h-19.25">
                  {zoomable ? (
                    <div
                      className={cn(
                        'pointer-events-auto absolute inset-y-0 left-4 z-1002 flex items-center gap-2.5',
                        chromeClass(chromeHidden),
                      )}
                    >
                      <div
                        className={cn(
                          GLASS_CLASS,
                          'flex size-10 items-center justify-center',
                        )}
                      >
                        <GlassButton
                          label={copy.rotate}
                          onClick={() =>
                            setRotation((value) => (value - 90 + 360) % 360)
                          }
                        >
                          <Icon
                            spec={{
                              type: 'builtin',
                              id: 'i-arrow-rotate-left',
                            }}
                            size={24}
                          />
                        </GlassButton>
                      </div>
                      <div
                        className={cn(
                          GLASS_CLASS,
                          'flex h-10 items-center px-2',
                        )}
                      >
                        <GlassButton
                          label={copy.zoomOut}
                          disabled={zoom <= minZoom}
                          onClick={() => zoomRef.current?.zoomOut()}
                        >
                          <MinusIcon className="size-6" />
                        </GlassButton>
                        <input
                          aria-label={copy.zoom}
                          type="range"
                          min={minZoom}
                          max={maxZoom}
                          step={(maxZoom - minZoom) / 100 || 0.01}
                          value={Math.min(maxZoom, Math.max(minZoom, zoom))}
                          onChange={(event) =>
                            zoomRef.current?.changeZoom(
                              Number(event.target.value),
                            )
                          }
                          className={RANGE_CLASS}
                          style={
                            {
                              '--media-range': `${
                                ((zoom - minZoom) / (maxZoom - minZoom || 1)) *
                                100
                              }%`,
                            } as CSSProperties
                          }
                        />
                        <GlassButton
                          label={copy.zoomIn}
                          disabled={zoom >= maxZoom}
                          onClick={() => zoomRef.current?.zoomIn()}
                        >
                          <PlusIcon className="size-6" />
                        </GlassButton>
                      </div>
                      <div
                        className={cn(
                          GLASS_CLASS,
                          'flex size-10 items-center justify-center',
                          !transformed && 'invisible',
                        )}
                      >
                        <GlassButton
                          label={copy.reset}
                          onClick={() => {
                            setRotation(0);
                            const minimum = zoomRef.current?.minZoom ?? 1;
                            zoomRef.current?.changeZoom(minimum, true);
                            setZoom(minimum);
                          }}
                        >
                          <XIcon className="size-5" />
                        </GlassButton>
                      </div>
                    </div>
                  ) : active?.kind === 'video' && active.url ? (
                    <div
                      className={cn(
                        'pointer-events-auto absolute inset-y-0 left-4 z-1002 flex items-center gap-2.5',
                        chromeClass(chromeHidden),
                      )}
                    >
                      <div
                        className={cn(
                          GLASS_CLASS,
                          'flex size-10 items-center justify-center',
                        )}
                      >
                        <GlassButton
                          label={videoMuted ? copy.unmute : copy.mute}
                          onClick={() => setVideoMuted((value) => !value)}
                        >
                          {videoMuted ? (
                            <Icon
                              spec={{ type: 'builtin', id: 'i-volume-mute' }}
                              size={24}
                            />
                          ) : (
                            <TransportVolumeGlyph />
                          )}
                        </GlassButton>
                      </div>
                      <div
                        className={cn(
                          GLASS_CLASS,
                          'flex h-10 items-center py-0.5 pr-3.5 pl-2',
                        )}
                      >
                        <GlassButton
                          label={videoPaused ? copy.play : copy.pause}
                          onClick={() => {
                            const node = videoRef.current;
                            if (!node) {
                              return;
                            }
                            if (node.paused) {
                              void node.play();
                            } else {
                              node.pause();
                            }
                          }}
                        >
                          {videoPaused ? (
                            <TransportPlayGlyph />
                          ) : (
                            <VoicePauseBars />
                          )}
                        </GlassButton>
                        <span
                          className="text-[13px] leading-[normal] text-white tabular-nums"
                          style={{ minWidth: clockSlot }}
                        >
                          {formatClock(videoCurrentTime)}
                        </span>
                        <input
                          aria-label={copy.progress}
                          type="range"
                          min={0}
                          max={videoDuration || 0}
                          step={0.01}
                          value={Math.min(videoCurrentTime, videoDuration || 0)}
                          disabled={!videoDuration}
                          onChange={(event) => {
                            const value = Number(event.target.value);
                            if (videoRef.current) {
                              videoRef.current.currentTime = value;
                            }
                            setVideoCurrentTime(value);
                          }}
                          className={RANGE_CLASS}
                          style={
                            {
                              '--media-range': `${
                                (videoCurrentTime / (videoDuration || 1)) * 100
                              }%`,
                            } as CSSProperties
                          }
                        />
                        <span className="text-[13px] leading-[normal] text-white">
                          {formatClock(videoDuration)}
                        </span>
                      </div>
                      <div
                        className={cn(
                          GLASS_CLASS,
                          'flex size-10 items-center justify-center',
                        )}
                      >
                        <GlassButton
                          label={videoExpanded ? copy.restore : copy.expand}
                          onClick={() => setVideoExpanded((value) => !value)}
                        >
                          <Icon
                            spec={{
                              type: 'builtin',
                              id: videoExpanded ? 'i-zoom-out' : 'i-zoom-in',
                            }}
                            size={24}
                          />
                        </GlassButton>
                      </div>
                    </div>
                  ) : null}
                  {items.length > 1 ? (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4">
                      <div
                        ref={setThumbRow}
                        className="pointer-events-auto flex max-w-full items-center gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/35"
                      >
                        {items.map((item, itemIndex) => {
                          const thumb =
                            item.poster ||
                            item.thumbnail ||
                            (item.kind === 'image' ? item.url : undefined);
                          return (
                            <button
                              key={item.id}
                              type="button"
                              aria-label={`${itemIndex + 1}`}
                              aria-current={itemIndex === index}
                              onClick={() => select(itemIndex)}
                              className={cn(
                                'relative size-14 shrink-0 cursor-pointer overflow-hidden rounded-[10px] border border-white/30 bg-white/8 p-0',
                                itemIndex === index &&
                                  'size-15.5 border-[2.4px] border-indigo-500/90',
                              )}
                            >
                              {thumb ? (
                                <img
                                  src={thumb}
                                  alt=""
                                  className="size-full object-cover"
                                />
                              ) : null}
                              {item.kind === 'video' ? (
                                <ThumbPlayBadge />
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
                {caption ? (
                  <div
                    className={cn(
                      'pointer-events-none absolute inset-x-4 bottom-22.25 z-1002 flex justify-center',
                      chromeClass(chromeHidden),
                    )}
                  >
                    {/* Figma 598:17170 Context Menu：圆角 20，高 130（五行 22 + 上下 padding 10）。
                        滑条宽 12，右缘距卡片 4，画在圆角里面。 */}
                    <div
                      data-media-lightbox-caption
                      className="pointer-events-auto flex max-h-32.5 min-w-0 w-fit max-w-full flex-col overflow-hidden rounded-[20px] bg-[rgba(57,57,57,0.86)] py-2.5 pr-1 pl-2.5 text-left text-[14px] leading-5.5 text-white"
                    >
                      <div
                        ref={setCaptionNode}
                        className="min-h-0 overflow-x-hidden overflow-y-auto pr-2 whitespace-pre-wrap break-words [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/35"
                      >
                        {caption}
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            ),
            slide: ({ slide, offset }) => {
              if ('pending' in slide && slide.pending) {
                const poster = 'poster' in slide ? slide.poster : undefined;
                return (
                  <div className="flex h-full w-full items-center justify-center">
                    {typeof poster === 'string' && poster ? (
                      <img
                        src={poster}
                        alt=""
                        className="size-full object-contain"
                      />
                    ) : (
                      <span className="text-[15px] text-white/70">
                        {copy.loading}
                      </span>
                    )}
                  </div>
                );
              }
              if ('failed' in slide && slide.failed) {
                const failedId = 'itemId' in slide ? slide.itemId : undefined;
                const failedItem =
                  typeof failedId === 'string'
                    ? items.find((item) => item.id === failedId)
                    : undefined;
                return (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-4 px-6 text-center text-[15px] text-white/65">
                    <span>{copy.unavailable}</span>
                    {onRetry && failedItem ? (
                      <button
                        type="button"
                        onClick={() => onRetry(failedItem)}
                        className="cursor-pointer rounded-full bg-white/12 px-4 py-2 text-[13px] text-white hover:bg-white/18"
                      >
                        {copy.retry}
                      </button>
                    ) : null}
                  </div>
                );
              }
              if (slide.type === 'video') {
                const source = slide.sources[0];
                return source ? (
                  <div className="relative h-full w-full">
                    <video
                      ref={
                        offset === 0
                          ? bindVideoRef.current
                          : (node) => {
                              node?.pause();
                            }
                      }
                      src={source.src}
                      playsInline
                      autoPlay={offset === 0}
                      muted={offset === 0 ? videoMuted : true}
                      preload="metadata"
                      onClick={() => {
                        const node = videoRef.current;
                        if (!node || offset !== 0) {
                          return;
                        }
                        if (node.paused) {
                          wantPlayRef.current = true;
                          void node.play();
                        } else {
                          wantPlayRef.current = false;
                          node.pause();
                        }
                      }}
                      onPlay={() => offset === 0 && setVideoPaused(false)}
                      onPause={() => offset === 0 && setVideoPaused(true)}
                      onCanPlay={(event) => {
                        if (offset !== 0 || !wantPlayRef.current) {
                          return;
                        }
                        void event.currentTarget.play().catch(() => {
                          setVideoPaused(true);
                        });
                      }}
                      className="size-full object-contain"
                    />
                    {videoPaused && offset === 0 ? (
                      <button
                        type="button"
                        aria-label={copy.play}
                        onClick={() => void videoRef.current?.play()}
                        className="absolute top-1/2 left-1/2 flex size-20 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-[1.667px] border-white/10 bg-black/40 text-white shadow-[0_3.333px_10px_rgba(0,0,0,0.04)]"
                      >
                        {/* Figma 598:16956 Media States：80 圆、无环三角 43.333、填充 80% 白。 */}
                        <CenterPlayGlyph />
                      </button>
                    ) : null}
                  </div>
                ) : null;
              }
              return undefined;
            },
          }}
        />
      ) : null}
    </div>
  );
  if (contained || !portal) {
    return stage;
  }
  return createPortal(stage, portal);
}

function thumbWheelDelta(event: WheelEvent, row: HTMLElement): number {
  if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.deltaY === 0) {
    return 0;
  }
  const max = row.scrollWidth - row.clientWidth;
  if (max <= 1) {
    return 0;
  }
  const next = Math.max(0, Math.min(max, row.scrollLeft + event.deltaY));
  return next - row.scrollLeft;
}

function formatClock(value: number): string {
  if (!Number.isFinite(value) || value < 0) {
    return '00:00';
  }
  const seconds = Math.floor(value);
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function toSlide(item: MediaLightboxItem): Slide | FailedSlide | VideoSlide {
  if (item.pending || !item.url) {
    return {
      src: '',
      alt: item.name,
      pending: item.pending === true,
      failed: item.pending !== true,
      itemId: item.id,
      poster: item.poster || item.thumbnail,
    };
  }
  if (item.kind === 'video') {
    return {
      type: 'video',
      sources: [{ src: item.url, type: item.mime }],
      poster: item.poster || item.thumbnail,
    };
  }
  return { src: item.url, alt: item.name };
}

/** Figma 598:16866。实心喇叭收成 13×21，不含声波。 */
function TransportVolumeGlyph() {
  return (
    <svg width={13} height={21} viewBox="0 0 13 21" aria-hidden="true">
      <path
        d="M13 11.3499V9.65013C13 6.03439 13 4.22652 12.0731 3.40576C11.7163 3.08978 11.2835 2.87196 10.8171 2.77367C9.60569 2.51836 8.15379 3.59557 5.25 5.75V15.25C8.15379 17.4044 9.60569 18.4816 10.8171 18.2263C11.2835 18.128 11.7163 17.9102 12.0731 17.5942C13 16.7735 13 14.9656 13 11.3499Z"
        fill="currentColor"
      />
      <path
        d="M0 10V10.9581C0 11.9181 0 12.3981 0.119216 12.7896C0.382991 13.6561 1.05646 14.3371 1.9199 14.6104C2.31014 14.734 2.79009 14.7393 3.75 14.75V6.25008C2.8198 6.25006 2.3547 6.25005 1.97534 6.36142C1.07732 6.62506 0.375041 7.32733 0.11138 8.22534C0 8.6047 0 9.0698 0 10Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Figma 598:16872。20px 实心圆，三角是挖空。稿上的 backdrop-blur 不加。
 * 图标库的 play-circle 是描边圆，对不上这块。
 */
function TransportPlayGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden="true">
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M10 0C15.53 0 20 4.48 20 10C20 15.52 15.52 20 10 20C4.48 20 0 15.52 0 10C0 4.48 4.48 0 10 0ZM8.99023 6.25391C8.09447 5.73673 6.97461 6.38363 6.97461 7.41797V12.6094C6.97468 13.6437 8.0945 14.2896 8.99023 13.7725L13.4854 11.1768C14.3811 10.6596 14.3811 9.36678 13.4854 8.84961L8.99023 6.25391Z"
      />
    </svg>
  );
}

/**
 * 底部视频缩略图上的播放标（Figma 598:17530 / 598:16898）。
 * 圆 32，填充 Color/button/Media/gray00 40%，描边 0.667 Color/stroke/gray100 60%，
 * 阴影 0 1.333 4 / 4%。三角 17.333、白 80%。圆上的 backdrop-blur 不加。
 */
function ThumbPlayBadge() {
  return (
    <span className="pointer-events-none absolute top-1/2 left-1/2 flex size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[0.667px] border-white/10 bg-black/40 text-white shadow-[0_1.333px_4px_rgba(0,0,0,0.04)]">
      <svg
        width="17.333"
        height="17.333"
        viewBox="0 0 35 35"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M29.2899 13.6814C32.2291 15.3783 32.2291 19.6207 29.2899 21.3177L14.5373 29.8351C11.5981 31.532 7.92411 29.4108 7.92411 26.0169V8.9821C7.92411 5.5882 11.5981 3.467 14.5373 5.16396L29.2899 13.6814Z"
          fill="currentColor"
          fillOpacity="0.8"
        />
      </svg>
    </span>
  );
}

/**
 * 语音消息的暂停竖条（Figma 3422:29389）。两根 4.75×13.3 的胶囊，画布 19。
 * 预览条上跟其它图标一样用当前色（白），不跟气泡里的品牌色。
 */
function VoicePauseBars() {
  return (
    <svg width={19} height={19} viewBox="0 0 19 19" aria-hidden="true">
      <rect
        x="2.85"
        y="2.85"
        width="4.75"
        height="13.3"
        rx="2.375"
        fill="currentColor"
      />
      <rect
        x="11.4"
        y="2.85"
        width="4.75"
        height="13.3"
        rx="2.375"
        fill="currentColor"
      />
    </svg>
  );
}

/** Figma 598:16956 的播放三角。图标库的 play-circle 自带外环，和这块稿不一致。 */
function CenterPlayGlyph() {
  return (
    <svg
      width="43.333"
      height="43.333"
      viewBox="0 0 35 35"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M29.2899 13.6814C32.2291 15.3783 32.2291 19.6207 29.2899 21.3177L14.5373 29.8351C11.5981 31.532 7.92411 29.4108 7.92411 26.0169V8.9821C7.92411 5.5882 11.5981 3.467 14.5373 5.16396L29.2899 13.6814Z"
        fill="currentColor"
        fillOpacity="0.8"
      />
    </svg>
  );
}

function GlassButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-sm text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
    >
      {children}
    </button>
  );
}

function NavButton({
  label,
  direction,
  disabled,
  onClick,
}: {
  label: string;
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'yarl__button',
        direction === 'prev'
          ? 'yarl__navigation_prev'
          : 'yarl__navigation_next',
      )}
    >
      <Icon
        spec={{
          type: 'builtin',
          id: direction === 'prev' ? 'i-chevron-left' : 'i-chevron-right',
        }}
        size={32}
      />
    </button>
  );
}
